'use memo';

import type { LoginFlow, UpdateLoginFlowBody } from '@ory/client';
import { useState, useCallback } from 'react';
import type { AuthType } from '@/features/auth/components/GlobalAuthModal';
import { signInService } from '@/lib/auth/ory/service';
import { toSafeRedirectTarget } from '@/lib/navigation/internal-path';
import { navigateToUrl } from '@/utils/navigateToUrl';
type UseReauthFlowProps = {
  onSuccess?: () => void;
  enabled?: boolean;
  reauthType?: AuthType;
};

/**
 * 再認証フロー管理フック
 *
 * 再認証フローの初期化、送信処理、状態管理を行います。
 * - refresh=true: パスワード / OIDC による再認証
 * - aal=aal2: MFA（ステップアップ認証）による再認証
 *
 * OIDC 再認証の場合は IdP へのフルページリダイレクトが発生する。
 * リダイレクト後はセッションが更新されるため、再認証は再度要求されない。
 *
 * OIDC の method 注入は AuthMethodTabs 側で行うため、このフックでは行わない。
 */
export function useReauthFlow({
  onSuccess,
  enabled = true,
  reauthType = 'refresh'
}: UseReauthFlowProps = {}) {
  const [flow, setFlow] = useState<LoginFlow | null>(null);
  const [isPending, setIsPending] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const initFlow = useCallback(async () => {
    if (!enabled || flow || isInitializing) {
      return;
    }
    setIsInitializing(true);
    try {
      let initialFlow: LoginFlow;
      if (reauthType === 'aal2') {
        initialFlow = await signInService.initFlow({
          aal: 'aal2'
        });
      } else {
        initialFlow = await signInService.initFlow({
          refresh: true
        });
      }
      setFlow(initialFlow);
    } catch (error) {
      console.error('Failed to initialize reauth flow:', error);
      setFlow(null);
    } finally {
      setIsInitializing(false);
    }
  }, [enabled, flow, isInitializing, reauthType]);
  const onSubmit = useCallback(async (body: UpdateLoginFlowBody) => {
    if (!flow) {
      return;
    }
    setIsPending(true);
    try {
      const result = await signInService.submitFlow(flow.id, body);
      if (result.redirectTo) {
        // redirectTo は外部 IdP（OIDC 再認証）の絶対 URL か、内部の相対パス（フロー失効時の
        // '/auth/sign-in' 等）のいずれか。多層防御として内部パス→外部URL の順で検証する。
        const safeRedirect = toSafeRedirectTarget(result.redirectTo);
        if (safeRedirect) {
          navigateToUrl(safeRedirect);
          return;
        }
        console.error('Blocked unsafe redirectTo:', result.redirectTo);
        setIsPending(false);
        return;
      }
      if (result.error) {
        setIsPending(false);
        return;
      }
      if (result.data) {
        if (result.data.ui && result.data.ui.nodes.length > 0) {
          setFlow(result.data);
          setIsPending(false);
          return;
        }
        setIsPending(false);
        onSuccess?.();
        return;
      }
      setIsPending(false);
      onSuccess?.();
    } catch (error) {
      console.error('Reauth error:', error);
      setIsPending(false);
    }
  }, [flow, onSuccess]);
  const resetFlow = useCallback(() => {
    setFlow(null);
    setIsPending(false);
    setIsInitializing(false);
  }, []);
  return {
    flow,
    isPending,
    isInitializing,
    onSubmit,
    initFlow,
    resetFlow
  };
}